import {
  KycVerificationData,
  KycStatus,
  KycDocument,
  KycStep,
} from '../types/kyc'
import { apiRequest } from '../utils/api'
const API_BASE_URL = 'https://api.diasporahomes.com/v1'
/**
 * Service for handling KYC verification API requests
 */
export const kycService = {
  /**
   * Submit personal information (Step 1)
   * @param userData Personal information data
   */
  submitPersonalInfo: async (userData: {
    firstName: string
    lastName: string
    email: string
    phone: string
    dateOfBirth: string
    nationality: string
  }): Promise<{ success: boolean; stepId: string }> => {
    return apiRequest(`${API_BASE_URL}/kyc/personal-info`, {
      method: 'POST',
      body: JSON.stringify(userData),
    })
  },
  /**
   * Submit ID verification documents (Step 2)
   * @param idData ID verification data
   */
  submitIdVerification: async (idData: {
    stepId: string
    idType: string
    frontImage: File
    backImage: File
  }): Promise<{ success: boolean; documentIds: string[] }> => {
    const formData = new FormData()
    formData.append('stepId', idData.stepId)
    formData.append('idType', idData.idType)
    formData.append('frontImage', idData.frontImage)
    formData.append('backImage', idData.backImage)
    return apiRequest(`${API_BASE_URL}/kyc/id-verification`, {
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type here as it will be set automatically for FormData
      },
    })
  },
  /**
   * Submit proof of address document (Step 3)
   * @param addressData Address verification data
   */
  submitAddressVerification: async (addressData: {
    stepId: string
    documentType: string
    addressDocument: File
  }): Promise<{ success: boolean; documentId: string }> => {
    const formData = new FormData()
    formData.append('stepId', addressData.stepId)
    formData.append('documentType', addressData.documentType)
    formData.append('addressDocument', addressData.addressDocument)
    return apiRequest(`${API_BASE_URL}/kyc/address-verification`, {
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type here as it will be set automatically for FormData
      },
    })
  },
  /**
   * Submit final verification (Step 4)
   * @param finalData Final submission data
   */
  submitFinalVerification: async (finalData: {
    stepId: string
    consent: boolean
  }): Promise<{ success: boolean; verificationId: string }> => {
    return apiRequest(`${API_BASE_URL}/kyc/submit`, {
      method: 'POST',
      body: JSON.stringify(finalData),
    })
  },
  /**
   * Get verification status
   * @param userId User ID
   */
  getVerificationStatus: async (userId: string): Promise<KycStatus> => {
    return apiRequest(`${API_BASE_URL}/kyc/status/${userId}`, {
      method: 'GET',
    })
  },
  /**
   * Get verification details
   * @param verificationId Verification ID
   */
  getVerificationDetails: async (
    verificationId: string,
  ): Promise<KycVerificationData> => {
    return apiRequest(`${API_BASE_URL}/kyc/details/${verificationId}`, {
      method: 'GET',
    })
  },
  /**
   * Get document by ID
   * @param documentId Document ID
   */
  getDocument: async (documentId: string): Promise<KycDocument> => {
    return apiRequest(`${API_BASE_URL}/kyc/documents/${documentId}`, {
      method: 'GET',
    })
  },
  /**
   * Upload a document
   * @param stepId Step ID
   * @param documentType Document type
   * @param file Document file
   */
  uploadDocument: async (
    stepId: string,
    documentType: string,
    file: File,
  ): Promise<{ success: boolean; documentId: string }> => {
    const formData = new FormData()
    formData.append('stepId', stepId)
    formData.append('documentType', documentType)
    formData.append('file', file)
    return apiRequest(`${API_BASE_URL}/kyc/documents/upload`, {
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type here as it will be set automatically for FormData
      },
    })
  },
  /**
   * Delete a document
   * @param documentId Document ID
   */
  deleteDocument: async (documentId: string): Promise<{ success: boolean }> => {
    return apiRequest(`${API_BASE_URL}/kyc/documents/${documentId}`, {
      method: 'DELETE',
    })
  },
  /**
   * Get verification requirements by country
   * @param countryCode ISO country code
   */
  getCountryRequirements: async (
    countryCode: string,
  ): Promise<{
    idTypes: string[]
    addressDocumentTypes: string[]
    additionalRequirements?: string[]
  }> => {
    return apiRequest(`${API_BASE_URL}/kyc/requirements/${countryCode}`, {
      method: 'GET',
    })
  },
}
